import { ApolloClient, useApolloClient } from '@apollo/client';
import {
  Box,
  Button,
  Flex,
  Heading,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Skeleton,
  Tabs,
  Tab,
  TabList,
  useToast,
  Checkbox,
} from '@chakra-ui/react';
import React, { useEffect, useMemo, useState } from 'react';

import { Util } from '@finley-technologies/finley-types';

import {
  Maybe,
  useGetAssetVerificationReceivablesByMetadataQuery,
  useGetAssetVerificationReceivableHistoryLazyQueryGQLType,
  useGetStoredAssetVerificationReceivablesByMetadataQuery,
  useGetIsAssetVerificationRequiredQuery,
} from 'types/dashboard-core-gqlTypes.generated.d';

import {
  errorToastConfig,
  successToastConfig,
} from '../../common-frontend/constants';
import {
  getMockUiVisibilityMode,
  useCreditFacilityUiVisibility,
} from '../../common-frontend/hooks/useUiVisibility';
import { GET_RECEIVABLE_DOCUMENTATION_UPLOAD_SIGNED_URL } from '../../files/requests/files.gql';
import { presignedPostUpload } from '../../files/ui/utils';
import { UploadDataTapeForm } from '../../manualDataTapeUploads/ui/UploadDataTapeForm';
import { AssetVerificationReceivableHistory } from './AssetVerificationReceivablesHistory';
import { AssetVerificationReceivablesSection } from './AssetVerificationReceivablesTable';
import { EditAssetVerification } from './AssetVerificationReceivablesTable/types';
import { transformAssetVerificationReportToAssetVerificationReceivableContainer } from './AssetVerificationReceivablesTable/utils';
import { getUnexpectedErrorMessage } from 'packages/common-frontend/utils';
import { useFeatureFlags } from '~/contexts/FeatureFlagProvider';
import { Icon } from '~/packages/common-frontend/components/icons';

// @jendevelops TODO-P4: create generic check component
type CheckAssetVerificationRequiredProps = {
  creditFacilityId: string;
  artifactDocumentId: string;
  children: React.ReactNode;
};
const CheckAssetVerificationRequired = ({
  creditFacilityId,
  artifactDocumentId,
  children,
}: CheckAssetVerificationRequiredProps) => {
  const toast = useToast();
  const { data, loading } = useGetIsAssetVerificationRequiredQuery({
    /**
     * Programmatically added fetchPolicy.
     * @fetchPolicy-TODO: Explicitly handle fetch policy for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
    variables: { creditFacilityId, artifactDocumentId },
  });

  if (!data || loading || !data.isAssetVerificationRequired) {
    // @jendevelops TODO-AssetVerification - create skeleton for asset verification section
    return <Skeleton />;
  }

  // need to transform data => add `summaryCounts`, `baseFieldConfigs` can come off a different gql query
  return data.isAssetVerificationRequired && <>{children}</>;
};

const AssetVerificationSectionKeysIterable = [
  'newlyAssignedReceivables',
  'historicalAssignedReceivables',
] as const;
type AssetVerificationSectionKeysType = typeof AssetVerificationSectionKeysIterable[number];
const AssetVerificationSectionKeys = {
  newlyAssignedReceivables: 'newlyAssignedReceivables',
  historicalAssignedReceivables: 'historicalAssignedReceivables',
} as const;
// eslint-disable-next-line no-redeclare
type AssetVerificationSectionKeys = AssetVerificationSectionKeysType;
type AssetVerificationSectionTabType = {
  key: AssetVerificationSectionKeys;
  label: string;
};
const assetVerificationSectionTabOptions: Record<
  AssetVerificationSectionKeys,
  AssetVerificationSectionTabType
> = {
  newlyAssignedReceivables: {
    key: AssetVerificationSectionKeys.newlyAssignedReceivables,
    label: 'Newly Assigned Receivables',
  },
  historicalAssignedReceivables: {
    key: AssetVerificationSectionKeys.historicalAssignedReceivables,
    label: 'Historical Receivables',
  },
};

type UploadStatus = 'inProgress' | 'none';

const uploadReceivableDocumentation = async ({
  client,
  toast,
  creditFacilityId,
  file,
  uploadStatusCallback,
}: {
  client: ApolloClient<object>;
  toast: ReturnType<typeof useToast>;
  creditFacilityId: string;
  file: File;
  uploadStatusCallback: (status: UploadStatus) => void;
}) => {
  try {
    uploadStatusCallback('inProgress');
    await presignedPostUpload<
      { creditFacilityId: string; filename: string },
      {}
    >({
      client,
      file,
      query: GET_RECEIVABLE_DOCUMENTATION_UPLOAD_SIGNED_URL,
      queryVariables: { creditFacilityId, filename: file.name },
    });
    toast({
      ...successToastConfig,
      title: 'A new asset verification report was uploaded.',
    });
  } catch (err: any) {
    toast({
      ...errorToastConfig,
      title:
        'Error uploading asset verification report. Please try again, and contact your Finley representative if this persists.',
    });
  } finally {
    uploadStatusCallback('none');
  }
};

type AssetVerificationXlsxDocumentationUploadModalProps = {
  creditFacilityId: string;
  isOpen: boolean;
  onClose: () => void;
  uploadStatusCallback: (status: UploadStatus) => void;
};
const AssetVerificationXlsxDocumentationUploadModal = ({
  creditFacilityId,
  isOpen,
  onClose,
  uploadStatusCallback,
}: AssetVerificationXlsxDocumentationUploadModalProps) => {
  const client = useApolloClient();
  const toast = useToast();
  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Upload New Asset Verification Report</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <UploadDataTapeForm
            allowedExtensions={['xlsx']}
            onClose={onClose}
            handleFile={(file) =>
              uploadReceivableDocumentation({
                client,
                toast,
                file,
                uploadStatusCallback,
                creditFacilityId,
              })
            }
          />
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

type AssetVerificationSectionHeaderProps = {
  creditFacilityId: string;
};
const AssetVerificationSectionHeader = ({
  creditFacilityId,
}: AssetVerificationSectionHeaderProps) => {
  const flags = useFeatureFlags();
  // @JonAtFinley @danitian replace with real UiVisibility returned from auth
  const mockUiVisibilityMode = getMockUiVisibilityMode({
    flags,
    defaultTo: 'lenderOwnerWithoutBorrower',
  });
  const facilityUiVisibility = useCreditFacilityUiVisibility({
    mockUiVisibilityMode,
    creditFacilityId,
  });
  const [isOpen, setIsOpen] = useState(false);
  const [uploadStatus, setUploadStatus] = useState<UploadStatus>('none');
  return (
    <>
      <AssetVerificationXlsxDocumentationUploadModal
        creditFacilityId={creditFacilityId}
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        uploadStatusCallback={setUploadStatus}
      />
      <Flex
        alignItems="center"
        justifyContent="space-between"
        marginBottom="1rem"
      >
        <Heading as="h4" fontSize="20px">
          Asset Verification
        </Heading>
        {facilityUiVisibility.verification
          .showAssetVerificationReportUpload && (
          <Button
            isLoading={uploadStatus === 'inProgress'}
            leftIcon={<Icon icon="upload" />}
            colorScheme="gray"
            variant="v2-ghost"
            size="sm"
            onClick={() => setIsOpen(true)}
          >
            Upload New Report
          </Button>
        )}
      </Flex>
    </>
  );
};

type VerificationAssetVerificationSectionTableProps = {
  creditFacilityId: string;
  artifactDocumentId: string;
  adminMode: boolean;
  assetVerificationReportByMetadata: Maybe<string>;
  reportingDate: Util.FullDateStr;
  fileVersion: number;
  onEdit?: EditAssetVerification;
};
const VerificationAssetVerificationSectionTable = ({
  assetVerificationReportByMetadata,
  creditFacilityId,
  artifactDocumentId,
  reportingDate,
  fileVersion,
  adminMode,
  onEdit = () => {},
}: VerificationAssetVerificationSectionTableProps) => {
  const toast = useToast();
  const [selectedTab, setSelectedTab] = useState<AssetVerificationSectionKeys>(
    AssetVerificationSectionKeys.newlyAssignedReceivables,
  );
  const [showSampled, setShowSampled] = useState<boolean>(false);

  const [
    getAssetVerificationReceivableHistory,
    {
      data: assetVerificationReceivableHistoryData,
      loading: assetVerificationReceivableHistoryLoading,
    },
  ] = useGetAssetVerificationReceivableHistoryLazyQueryGQLType({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
  });

  useEffect(() => {
    if (!adminMode) {
      getAssetVerificationReceivableHistory({
        variables: {
          creditFacilityId,
          reportingDate,
          fileVersion,
          artifactDocumentId,
        },
      });
    }
  }, [
    adminMode,
    artifactDocumentId,
    creditFacilityId,
    fileVersion,
    getAssetVerificationReceivableHistory,
    reportingDate,
  ]);

  const receivablesVerification = useMemo(() => {
    if (!assetVerificationReportByMetadata) {
      return null;
    }
    return transformAssetVerificationReportToAssetVerificationReceivableContainer(
      assetVerificationReportByMetadata,
      showSampled,
    );
  }, [assetVerificationReportByMetadata, showSampled]);

  const tabs = [
    assetVerificationSectionTabOptions.newlyAssignedReceivables,
    assetVerificationSectionTabOptions.historicalAssignedReceivables,
  ];

  // need to transform data => add `summaryCounts`, `baseFieldConfigs` can come off a different gql query
  return (
    <Box mb="40px">
      <AssetVerificationSectionHeader creditFacilityId={creditFacilityId} />
      <Checkbox
        isChecked={showSampled}
        onChange={(e) => setShowSampled(e.target.checked)}
        ml="9px"
      >
        Show Only Sampled
      </Checkbox>
      {!adminMode && (
        <Tabs
          variant="line"
          index={tabs.findIndex(({ key }) => key === selectedTab)}
          colorScheme="blue"
          mt="32px"
          mb="32px"
        >
          <TabList>
            {tabs.map(({ label, key }) => (
              <Tab
                key={key}
                onMouseDown={() => {
                  setSelectedTab(key);
                }}
                fontSize="sm"
                mt="0px"
              >
                {label}
              </Tab>
            ))}
          </TabList>
        </Tabs>
      )}
      {selectedTab ===
        AssetVerificationSectionKeys.newlyAssignedReceivables && (
        <AssetVerificationReceivablesSection
          adminMode={adminMode}
          onEdit={onEdit}
          receivablesVerification={receivablesVerification}
          artifactDocumentId={artifactDocumentId}
          creditFacilityId={creditFacilityId}
          reportingDate={reportingDate}
          fileVersion={fileVersion}
        />
      )}
      {selectedTab ===
        AssetVerificationSectionKeys.historicalAssignedReceivables && (
        <AssetVerificationReceivableHistory
          assetVerificationReceivableHistoryData={
            assetVerificationReceivableHistoryData
          }
          isDataLoading={assetVerificationReceivableHistoryLoading}
        />
      )}
    </Box>
  );
};

type VerificationAssetVerificationSectionTableDynamicProps = {
  creditFacilityId: string;
  reportingDate: Util.FullDateStr;
  artifactDocumentId: string;
  fileVersion: number;
  adminMode: boolean;
  loanProductId?: string;
  onEdit?: EditAssetVerification;
};
const VerificationAssetVerificationSectionTableDynamic = ({
  creditFacilityId,
  reportingDate,
  fileVersion,
  artifactDocumentId,
  loanProductId,
  adminMode,
  onEdit = () => {},
}: VerificationAssetVerificationSectionTableDynamicProps) => {
  const toast = useToast();
  const { data, loading } = useGetAssetVerificationReceivablesByMetadataQuery({
    /**
     * Programmatically added fetchPolicy.
     * @fetchPolicy-TODO: Explicitly handle fetch policy for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
    fetchPolicy: 'network-only',
    variables: {
      creditFacilityId,
      reportingDate,
      artifactDocumentId,
      loanProductId,
      fileVersion,
    },
  });

  const assetVerificationReportByMetadata = loading
    ? null
    : data?.assetVerificationReportByMetadata;

  return (
    <VerificationAssetVerificationSectionTable
      creditFacilityId={creditFacilityId}
      artifactDocumentId={artifactDocumentId}
      reportingDate={reportingDate}
      fileVersion={fileVersion}
      adminMode={adminMode}
      assetVerificationReportByMetadata={assetVerificationReportByMetadata}
      onEdit={onEdit}
    />
  );
};

type VerificationAssetVerificationSectionTableStoredProps = {
  creditFacilityId: string;
  reportingDate: Util.FullDateStr;
  artifactDocumentId: string;
  loanProductId?: string;
  fileVersion: number;
  adminMode: boolean;
  onEdit?: EditAssetVerification;
};
const VerificationAssetVerificationSectionTableStored = ({
  creditFacilityId,
  reportingDate,
  fileVersion,
  artifactDocumentId,
  loanProductId,
  adminMode,
  onEdit = () => {},
}: VerificationAssetVerificationSectionTableStoredProps) => {
  const toast = useToast();
  const {
    data,
    loading,
  } = useGetStoredAssetVerificationReceivablesByMetadataQuery({
    /**
     * Programmatically added fetchPolicy.
     * @fetchPolicy-TODO: Explicitly handle fetch policy for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
    fetchPolicy: 'network-only',
    variables: {
      creditFacilityId,
      reportingDate,
      artifactDocumentId,
      loanProductId,
      fileVersion,
    },
  });

  const assetVerificationReportByMetadata = loading
    ? null
    : data?.assetVerificationReportByMetadataStored;

  return (
    <VerificationAssetVerificationSectionTable
      creditFacilityId={creditFacilityId}
      artifactDocumentId={artifactDocumentId}
      adminMode={adminMode}
      reportingDate={reportingDate}
      fileVersion={fileVersion}
      assetVerificationReportByMetadata={assetVerificationReportByMetadata}
      onEdit={onEdit}
    />
  );
};

type VerificationAssetVerificationSectionProps = {
  artifactDocumentId: string;
  creditFacilityId: string;
  reportingDate: Util.FullDateStr;
  fileVersion: number;
  dynamicMode: boolean;
  adminMode?: boolean;
  loanProductId?: string;
  onEdit?: EditAssetVerification;
};
export const VerificationAssetVerificationSection = ({
  artifactDocumentId,
  creditFacilityId,
  reportingDate,
  fileVersion,
  loanProductId,
  onEdit,
  adminMode = false,
  dynamicMode = false,
}: VerificationAssetVerificationSectionProps) => {
  const commonProps = {
    artifactDocumentId,
    creditFacilityId,
    reportingDate,
    fileVersion,
    onEdit,
    adminMode,
    loanProductId,
  };
  return (
    <CheckAssetVerificationRequired
      artifactDocumentId={artifactDocumentId}
      creditFacilityId={creditFacilityId}
    >
      {dynamicMode ? (
        <VerificationAssetVerificationSectionTableDynamic {...commonProps} />
      ) : (
        <VerificationAssetVerificationSectionTableStored {...commonProps} />
      )}
    </CheckAssetVerificationRequired>
  );
};
